import { useEffect, useState, useCallback } from "react";
import Shell from "@/components/Shell";
import CopyButton from "@/components/CopyButton";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Server, Play, Pause, Gauge, RotateCcw, AlertTriangle, OctagonX, Lock, Globe, Trash2, CheckCircle2, XCircle, Clock } from "lucide-react";

function money(m) { return `GBP ${(((m || 0) / 100)).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

function Bar({ label, used, included }) {
  const pct = included ? Math.min(100, (used / included) * 100) : 0;
  const over = included && used > included;
  return (
    <div style={{ marginTop: 6 }} data-testid={`usage-bar-${label}`}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }} className="k-muted">
        <span>{label.replace(/_/g, " ")}</span><span>{used} / {included || "∞"} {over ? "· over" : `· ${pct.toFixed(0)}%`}</span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: "var(--control)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: over ? "var(--accent-secondary)" : pct >= 80 ? "var(--accent-secondary)" : "var(--accent-primary)" }} />
      </div>
    </div>
  );
}

const DOMAIN_STATUS = { Verified: { i: CheckCircle2, c: "k-tag-green" }, Pending: { i: Clock, c: "k-tag-warn" }, Failed: { i: XCircle, c: "k-tag-red" } };

function AccessControls({ dep, onChange }) {
  const [acc, setAcc] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [allowDom, setAllowDom] = useState("");
  const [domainInput, setDomainInput] = useState("");

  const loadAccess = useCallback(() => {
    api.get(`/hosting/${dep.id}/access`).then((r) => setAcc(r.data)).catch(() => {});
  }, [dep.id]);
  useEffect(loadAccess, [loadAccess]);

  const refresh = () => { loadAccess(); onChange(); };

  const addInvite = async () => {
    if (!inviteEmail.trim()) return;
    try { await api.post(`/hosting/${dep.id}/invites`, { email: inviteEmail.trim() }); setInviteEmail(""); toast.success("Invited — a personal link was emailed"); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const removeInvite = async (id) => {
    try { await api.delete(`/hosting/${dep.id}/invites/${id}`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const addAllow = async () => {
    if (!allowDom.trim()) return;
    try { await api.post(`/hosting/${dep.id}/allowed-domains`, { domain: allowDom.trim() }); setAllowDom(""); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const removeAllow = async (d) => {
    try { await api.post(`/hosting/${dep.id}/allowed-domains/remove`, { domain: d }); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const linkDomain = async () => {
    if (!domainInput.trim()) return;
    try { await api.post(`/hosting/${dep.id}/domain`, { domain: domainInput.trim() }); setDomainInput(""); toast.success("Domain linked — add the TXT record, then Check now"); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const verifyDomain = async () => {
    try { const { data } = await api.post(`/hosting/${dep.id}/domain/verify`); toast[data.ok ? "success" : "warning"](data.ok ? "Domain verified" : (data.detail || "Not verified yet")); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const unlinkDomain = async () => {
    try { const { data } = await api.delete(`/hosting/${dep.id}/domain`); toast.success(data.note || "Unlinked"); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const isPrivate = dep.state === "Private";
  const ds = acc?.custom_domain_status;
  const dsMeta = DOMAIN_STATUS[ds];

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--control)", paddingTop: 14 }} data-testid={`access-controls-${dep.id}`}>
      {/* Private access lists */}
      {isPrivate && (
        <div data-testid={`private-lists-${dep.id}`} style={{ marginBottom: 16 }}>
          <div className="k-muted" style={{ fontSize: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <Lock size={13} aria-hidden /> Only invited people and allowed email domains can open this app.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <strong style={{ fontSize: 13 }}>Invited people</strong>
              <div style={{ display: "flex", gap: 6, margin: "6px 0" }}>
                <input className="k-control" data-testid={`invite-email-${dep.id}`} type="email" placeholder="person@company.com"
                  value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} style={{ flex: 1 }} />
                <button className="k-btn k-btn-ghost" data-testid={`invite-add-${dep.id}`} onClick={addInvite}>Invite</button>
              </div>
              {(acc?.invites || []).length === 0 ? <p className="k-muted" style={{ fontSize: 12 }}>No one invited yet.</p> :
                (acc.invites || []).map((inv) => (
                  <div key={inv.id} data-testid={`invite-row-${inv.id}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "3px 0" }}>
                    <span style={{ flex: 1 }}>{inv.email}</span>
                    <button className="k-btn k-btn-ghost" data-testid={`invite-remove-${inv.id}`} onClick={() => removeInvite(inv.id)} style={{ padding: "4px 8px" }}><Trash2 size={13} /></button>
                  </div>
                ))}
            </div>
            <div>
              <strong style={{ fontSize: 13 }}>Allowed email domains</strong>
              <div style={{ display: "flex", gap: 6, margin: "6px 0" }}>
                <input className="k-control" data-testid={`allow-domain-${dep.id}`} placeholder="acme.com"
                  value={allowDom} onChange={(e) => setAllowDom(e.target.value)} style={{ flex: 1 }} />
                <button className="k-btn k-btn-ghost" data-testid={`allow-add-${dep.id}`} onClick={addAllow}>Allow</button>
              </div>
              {(acc?.allowed_domains || []).length === 0 ? <p className="k-muted" style={{ fontSize: 12 }}>No domains allowed yet.</p> :
                (acc.allowed_domains || []).map((d) => (
                  <div key={d} data-testid={`allow-row-${d}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "3px 0" }}>
                    <span style={{ flex: 1 }}>{d}</span>
                    <button className="k-btn k-btn-ghost" data-testid={`allow-remove-${d}`} onClick={() => removeAllow(d)} style={{ padding: "4px 8px" }}><Trash2 size={13} /></button>
                  </div>
                ))}
            </div>
          </div>
          {acc?.gate_url && <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>Private gate: <span className="mono">{acc.gate_url}</span></p>}
        </div>
      )}

      {/* Custom domain */}
      <div data-testid={`custom-domain-${dep.id}`}>
        <strong style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}><Globe size={13} aria-hidden /> Link your domain</strong>
        {!acc?.custom_domain ? (
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <input className="k-control" data-testid={`domain-input-${dep.id}`} placeholder="app.acme.com"
              value={domainInput} onChange={(e) => setDomainInput(e.target.value)} style={{ maxWidth: 260 }} />
            <button className="k-btn k-btn-primary" data-testid={`domain-link-${dep.id}`} onClick={linkDomain}>Link domain</button>
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span className="mono" style={{ fontSize: 13 }}>{acc.custom_domain}</span>
              {dsMeta && (
                <span className={dsMeta.c} data-testid={`domain-status-${dep.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                  <dsMeta.i size={13} aria-hidden /> {ds}
                </span>
              )}
              <button className="k-btn k-btn-ghost" data-testid={`domain-check-${dep.id}`} onClick={verifyDomain} style={{ padding: "5px 10px" }}>Check now</button>
              <button className="k-btn k-btn-ghost" data-testid={`domain-unlink-${dep.id}`} onClick={unlinkDomain} style={{ padding: "5px 10px" }}>Unlink</button>
            </div>
            {ds !== "Verified" && acc.custom_domain_txt_host && (
              <div className="k-container" data-testid={`domain-txt-${dep.id}`} style={{ padding: 12, marginTop: 8, background: "var(--canvas)" }}>
                <p className="k-muted" style={{ fontSize: 12, marginTop: 0 }}>Add this TXT record at your DNS provider, then press Check now. Nothing is linked until the record is found — there is no bypass.</p>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, flexWrap: "wrap" }}>
                  <span className="k-muted">Type</span><span className="mono">TXT</span>
                  <span className="k-muted">Host</span><span className="mono">{acc.custom_domain_txt_host}</span>
                  <CopyButton value={acc.custom_domain_txt_host} label="Copy host" testid={`copy-host-${dep.id}`} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginTop: 6, flexWrap: "wrap" }}>
                  <span className="k-muted">Value</span><span className="mono" style={{ wordBreak: "break-all" }}>{acc.custom_domain_txt_value}</span>
                  <CopyButton value={acc.custom_domain_txt_value} label="Copy value" testid={`copy-value-${dep.id}`} />
                </div>
              </div>
            )}
            {ds === "Verified" && (
              <p className="k-muted" style={{ fontSize: 12, marginTop: 6 }}>
                Ownership verified. SSL: {acc.custom_domain_ssl}. Live routing runs on the hosting adapter (sandbox until managed hosting goes live). Unlinking leaves the app on its subdomain.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Deployment({ dep, onChange }) {
  const [u, setU] = useState({ bandwidth_gb: 0, compute_hours: 0, storage_gb: 0 });
  const [status, setStatus] = useState(null);

  const act = async (path, body) => {
    try { const { data } = await api.post(`/hosting/${dep.id}/${path}`, body || {}); onChange(); return data; }
    catch (e) { toast.error(errMsg(e)); }
  };
  const publish = async () => { const d = await act("publish"); if (d) toast.success(d.note || "Published"); };
  const pause = async () => { const d = await act("pause"); if (d) toast.success("Paused"); };
  const setPrivate = async () => { const d = await act("private"); if (d) toast.success("App is now private"); };
  const rollback = async () => { const d = await act("rollback"); if (d) toast.success(d.note || "Rolled back"); };
  const report = async () => {
    try {
      const { data } = await api.post(`/hosting/${dep.id}/usage`, u);
      setStatus(data); onChange();
      toast[data.threshold === "ok" ? "success" : "warning"](`Usage: ${data.threshold}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const stateMeta = dep.state === "Published" ? { i: Play, label: "Published" }
    : dep.state === "Private" ? { i: Lock, label: "Private" }
    : dep.state === "Paused" ? { i: Pause, label: "Paused" } : null;

  return (
    <div className="k-container" data-testid={`deployment-${dep.id}`} style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <strong>{dep.subdomain || dep.id}</strong>
          {stateMeta && (
            <span className={`k-badge ${dep.state === "Published" ? "k-badge-accent" : "k-badge-sandbox"}`} data-testid={`state-badge-${dep.id}`}
              style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <stateMeta.i size={12} aria-hidden /> {stateMeta.label}
            </span>
          )}
          <span className="k-badge" style={{ marginLeft: 6 }}>{dep.region}</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="k-btn k-btn-primary" data-testid={`publish-${dep.id}`} onClick={publish}><Play size={14} /> Publish</button>
          <button className="k-btn k-btn-ghost" data-testid={`private-${dep.id}`} onClick={setPrivate}><Lock size={14} /> Private</button>
          <button className="k-btn k-btn-ghost" data-testid={`pause-${dep.id}`} onClick={pause}><Pause size={14} /> Pause</button>
          <button className="k-btn k-btn-ghost" data-testid={`rollback-${dep.id}`} onClick={rollback}><RotateCcw size={14} /> Rollback</button>
        </div>
      </div>
      <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>
        Tier {dep.plan_tier} · markup {dep.hosting_markup_pct}% · allowance price {money(dep.included_allowance_price_minor)} ·
        overage/GB {money((dep.overage_rates_minor || {}).bandwidth_gb)}
      </p>

      <AccessControls dep={dep} onChange={onChange} />

      {/* #121 usage metering dashboard */}
      <div data-testid={`usage-dashboard-${dep.id}`} style={{ marginTop: 14 }}>
        <Bar label="bandwidth_gb" used={dep.usage?.bandwidth_gb || 0} included={dep.allowances?.bandwidth_gb_included || 0} />
        <Bar label="compute_hours" used={dep.usage?.compute_hours || 0} included={dep.allowances?.compute_hours_included || 0} />
        <Bar label="storage_gb" used={dep.usage?.storage_gb || 0} included={dep.allowances?.storage_gb_included || 0} />
      </div>
      {/* #124 usage alerts */}
      {dep.usage_status && dep.usage_status !== "ok" && (
        <div className={`k-tag-${dep.usage_status === "paused_hard_ceiling" ? "red" : "warn"}`} data-testid={`usage-alert-${dep.id}`} style={{ fontSize: 12, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
          {dep.usage_status === "paused_hard_ceiling" ? <OctagonX size={13} aria-hidden="true" /> : <AlertTriangle size={13} aria-hidden="true" />}
          <span>{dep.usage_status === "notify_80pct" ? "Reached 80% of an included allowance." :
           dep.usage_status === "over_included" ? "Over included allowance — non-essential work throttled first; overage applies." :
           "Hard usage ceiling hit — deployment paused; visitors see a status page."}</span>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
        {["bandwidth_gb", "compute_hours", "storage_gb"].map((k) => (
          <input key={k} className="k-control" data-testid={`usage-${k}-${dep.id}`} type="number" placeholder={k}
            style={{ maxWidth: 140 }} value={u[k]} onChange={(e) => setU({ ...u, [k]: Number(e.target.value) })} />
        ))}
        <button className="k-btn k-btn-ghost" data-testid={`report-usage-${dep.id}`} onClick={report}><Gauge size={14} /> Report usage</button>
      </div>
      {status && (
        <div className={`k-tag-${status.threshold === "ok" ? "green" : status.threshold === "paused_hard_ceiling" ? "red" : "warn"}`}
          data-testid={`usage-status-${dep.id}`} style={{ marginTop: 8, fontSize: 13 }}>
          {status.threshold} — {status.message || ""} {status.estimated_overage_minor ? `(est. overage ${money(status.estimated_overage_minor)})` : ""}
        </div>
      )}
    </div>
  );
}

export default function Hosting() {
  const [regions, setRegions] = useState([]);
  const [deps, setDeps] = useState([]);
  const [form, setForm] = useState({ project_id: "", region: "", plan_tier: "free", custom_domain: "" });
  const [projects, setProjects] = useState([]);

  const load = () => {
    api.get("/hosting/regions").then((r) => { setRegions(r.data.regions || []); }).catch(() => {});
    api.get("/workspace/projects").then((r) => setProjects(r.data.projects || r.data || [])).catch(() => {});
  };
  useEffect(load, []);

  const loadDeps = (pid) => { if (pid) api.get(`/hosting/${pid}/status`).then((r) => setDeps(r.data.deployments || [])).catch(() => setDeps([])); };
  useEffect(() => { loadDeps(form.project_id); /* eslint-disable-next-line */ }, [form.project_id]);

  const provision = async () => {
    if (!form.project_id || !form.region) { toast.error("Pick a project and region"); return; }
    try { await api.post("/hosting/provision", form); toast.success("Provisioned (SANDBOX where real infra is absent)"); loadDeps(form.project_id); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Shell active="hosting">
      <h1 className="k-heading" style={{ fontSize: 30, marginTop: 0 }}>Managed hosting</h1>
      <p className="k-muted" style={{ marginTop: 6 }}></p>

      <div className="k-container" style={{ padding: 18, marginBottom: 18 }} data-testid="provision-form">
        <h3 className="k-heading" style={{ marginTop: 0, fontSize: 17 }}><Server size={16} /> Provision a deployment</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <select className="k-control" data-testid="hosting-project" style={{ maxWidth: 260 }}
            value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
            <option value="">Select project…</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className="k-control" data-testid="hosting-region" style={{ maxWidth: 220 }}
            value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
            <option value="">Region…</option>
            {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <select className="k-control" data-testid="hosting-tier" style={{ maxWidth: 160 }}
            value={form.plan_tier} onChange={(e) => setForm({ ...form, plan_tier: e.target.value })}>
            <option value="free">free</option><option value="pro">pro</option><option value="agency">agency</option>
          </select>
          <input className="k-control" data-testid="hosting-domain" placeholder="custom domain (optional)" style={{ maxWidth: 220 }}
            value={form.custom_domain} onChange={(e) => setForm({ ...form, custom_domain: e.target.value })} />
          <button className="k-btn k-btn-primary" data-testid="provision-btn" onClick={provision}>Provision</button>
        </div>
      </div>

      {deps.length === 0 ? <p className="k-muted" data-testid="no-deployments">No deployments for the selected project.</p> :
        deps.map((d) => <Deployment key={d.id} dep={d} onChange={() => loadDeps(form.project_id)} />)}
    </Shell>
  );
}
